import { useEffect, useState } from 'react'
import { useHistory } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { ICourseModel } from 'models/course.model'
import { enrolInitialState } from 'models/enrol.model'
import * as fromActions from 'store/actions/courses.action'
import Button from 'design-system/button'
import * as fromActionsEnrols from 'store/actions/enrols.action'
import { Sence } from 'components/integrations/Sence'
import { useIntegrations } from 'contexts/IntegrationsContext'
import { useAccess } from 'contexts/AccessContext'
import { createRouteMonitoringCourse } from 'enum/routes'
import { actionResolverService } from 'api/courses/actionResolver'
import ModalEnrol from './modal-enrol/ModalEnrol'
import styles from './EventTarget.module.scss'
import { actions } from './constants'

interface IAction {
  action: number
  enabled: boolean
  externalLink?: string
}

interface EventTargetProps {
  course: ICourseModel
  disabled?: boolean
  assigned: boolean
}

const EventTarget = ({ course, disabled }: EventTargetProps) => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()
  const { sence } = useIntegrations()
  const { loading: accessLoading } = useAccess()
  const [buttonText, setButtonText] = useState('')
  const [buttonEnabled, setButtonEnabled] = useState(false)
  const [buttonAction, setButtonAction] = useState(999)
  const [buttonLink, setButtonLink] = useState<string | undefined>('')
  const [showModalEnrol, setShowModalEnrol] = useState(false)

  const loading = (sence.isSence && sence.loading) || accessLoading

  useEffect(() => {
    if (!course.id) return

    actionResolverService
      .getAction(course.id)
      .then((response: { data: IAction }) => {
        setButtonAction(response.data.action)
        setButtonEnabled(response.data.enabled)
        setButtonLink(response.data.externalLink)

        switch (response.data.action) {
          case actions.noRequest:
            setButtonText(t('courses.show.content.request'))
            break
          case actions.pendingRequest:
            setButtonText(t('courses.show.content.pending'))
            break
          case actions.start:
            setButtonText(t('courses.show.content.initialize'))
            break
          case actions.continue:
            setButtonText(t('courses.show.content.continue'))
            break
          case actions.review:
            setButtonText(t('courses.show.content.review'))
            break
          case actions.monitoring:
            setButtonText(t('courses.show.content.monitoring'))
            break
          case actions.synchronous:
            setButtonText(
              response.data.externalLink
                ? t('courses.show.content.synchronous')
                : ''
            )
            break
          case actions.error:
            setButtonText('')
            break
        }
      })
  }, [course.id, t])

  const onButtonClick = () => {
    switch (buttonAction) {
      case actions.noRequest:
        createCourseRequest()
        break
      case actions.start:
        onStart()
        break
      case actions.pendingRequest:
      case actions.continue:
      case actions.review:
        goToCourseLessons()
        break
      case actions.monitoring:
        goToCourseMonitoring()
        break
      case actions.synchronous:
        goToExternalCourse()
        break
    }
  }

  const createCourseRequest = () => {
    setShowModalEnrol(true)
  }

  const onStart = () => {
    if (buttonLink) {
      course.id &&
        course.enrol &&
        dispatch({
          type: fromActions.COURSES_USER_COURSE_PROGRESS,
          payload: course.id,
        })
      window.open(buttonLink)
    } else {
      history.push(`/courses/${course.id}/lessons`)
    }
  }

  const goToCourseLessons = () => {
    if (buttonLink) {
      window.open(buttonLink)
    } else {
      history.push(`/courses/${course.id}/lessons`)
    }
  }

  const goToCourseMonitoring = () => {
    const url = createRouteMonitoringCourse({ id: `${course.id}` })
    window.location.href = url
  }

  const goToExternalCourse = () => {
    if (course.id && !course.enrol) {
      dispatch(
        fromActionsEnrols.Actions.enrolCreate({
          enrol: { ...enrolInitialState, courseId: course.id },
          getCourse: true,
          showToast: false,
          isSynchronous: !!course?.synchronousCourse,
        })
      )
    }
    window.open(buttonLink)
  }

  const showPendingButton = () => {
    setButtonText(t('courses.show.content.pending'))
    setButtonAction(actions.pendingRequest)
    setButtonEnabled(false)
    setShowModalEnrol(false)
  }

  return (
    <div className="d-flex justify-content-end">
      <div className={disabled ? styles.disabled : ''}>
        {sence.isSence && (
          <div className="mr-2">
            <Sence />
          </div>
        )}
        {loading && (
          <div className="spinner-border spinner-border-md text-primary" />
        )}
        {!loading && buttonText && (
          <Button
            onClick={onButtonClick}
            className="float-right m-0"
            disabled={!buttonEnabled}
          >
            {buttonText}
          </Button>
        )}
      </div>
      {showModalEnrol && (
        <ModalEnrol
          courseId={course.id}
          onAccept={showPendingButton}
          onClose={() => setShowModalEnrol(false)}
        />
      )}
    </div>
  )
}

export default EventTarget
